import { computed, reactive, type ComputedRef, type UnwrapNestedRefs } from 'vue';
import { useAuth } from 'src/composables/useAuth';
import { dtFormat } from 'src/lib/dateHelper';
import { MOVIE_SEARCH_RESULT_MODES } from 'src/consts/top';
import type { GIMovieList } from 'src/models/geoItem';
import type {
  CompareModeProps,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';
import type { RemainingMovieRatesByDateRange } from 'src/models/user';

interface Deps {
  parentMovieSearch: CompareModeProps['parentMovieSearch'];
}

interface MoviePlayerCompareModeMovieListContext {
  remainingMovieRatesByDateRanges: ComputedRef<RemainingMovieRatesByDateRange[] | undefined>;
  isCurrentMovieListLeftReady: ComputedRef<boolean>;
  isCurrentMovieListRightReady: ComputedRef<boolean>;
  isCurrentMovieListBothReady: ComputedRef<boolean>;
  playerControlCurrentTimeDispLeft: ComputedRef<string>;
  playerControlCurrentTimeDispRight: ComputedRef<string>;
  isMovieSearchResultModeDetail: ComputedRef<boolean>;
  isMovieSearchResultModeArea: ComputedRef<boolean>;
  setMoviePlayerControlDispVals: (lr: LeftRight, movieList: GIMovieList) => void;
}

export function useMoviePlayerCompareModeMovieList(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeMovieListContext> {
  const auth = useAuth();
  const remainingMovieRatesByDateRanges = computed(() =>
    auth.user?.settings.remainingMovieRatesByDateRanges);
  const isCurrentMovieListReady = (lr: LeftRight) => {
    const listSelection = state[lr].movieList.listSelection;
    return !!(listSelection.currentMovieListId && listSelection.currentMovieList);
  };
  const isCurrentMovieListLeftReady = computed(() => isCurrentMovieListReady('left'));
  const isCurrentMovieListRightReady = computed(() => isCurrentMovieListReady('right'));
  const isCurrentMovieListBothReady = computed(() =>
    !!isCurrentMovieListLeftReady.value && !!isCurrentMovieListRightReady.value);
  const playerControlCurrentTimeDispLeft = computed(() => {
    let ret = '00:00:00';
    if (!state.left.movieList.listSelection.currentMovieList) return ret;

    // ルート検索以外の場合は撮影時刻を表示
    ret = dtFormat(state.left.geo.currentMovieCurrentDt, 'HH:MM:SS');
    return ret;
  });
  const playerControlCurrentTimeDispRight = computed(() => {
    let ret = '00:00:00';
    if (!state.right.movieList.listSelection.currentMovieList) return ret;

    // ルート検索以外の場合は撮影時刻を表示
    ret = dtFormat(state.right.geo.currentMovieCurrentDt, 'HH:MM:SS');
    return ret;
  });
  const isMovieSearchResultModeDetail = computed(() => {
    return deps.parentMovieSearch.resultMode === MOVIE_SEARCH_RESULT_MODES.DETAIL;
  });
  const isMovieSearchResultModeArea = computed(() => {
    return deps.parentMovieSearch.resultMode === MOVIE_SEARCH_RESULT_MODES.AREA;
  });
  const setMoviePlayerControlDispVals = (lr: LeftRight, movieList: GIMovieList) => {
    // 1つ目のmovieのplayStartOffsetMsecを0位置として扱う
    const firstMovie = movieList.movies[0];
    const firstMovieStartOffsetDiffMsec =
      firstMovie.playStartOffsetMsec === undefined || firstMovie.hardStartOffsetMsec === undefined
        ? 0
        : firstMovie.playStartOffsetMsec - firstMovie.hardStartOffsetMsec;
    // なのでseek.minは0以下の数値となる
    state[lr].playback.seek.min = -firstMovieStartOffsetDiffMsec;
    state[lr].playback.seek.max = (movieList.fullDurationMsec ?? 0) - firstMovieStartOffsetDiffMsec;
    state[lr].playback.seek.current = firstMovie.playStartOffsetMsec || 0;
    state[lr].playback.seek.step = 250; // msec
    state[lr].movieList.timeDisp.startTimeDisp = dtFormat(movieList.hardStartTs, 'HH:MM:SS');
    state[lr].movieList.timeDisp.endTimeDisp = dtFormat(movieList.hardEndTs, 'HH:MM:SS');
  };

  return reactive({
    remainingMovieRatesByDateRanges,
    isCurrentMovieListLeftReady,
    isCurrentMovieListRightReady,
    isCurrentMovieListBothReady,
    playerControlCurrentTimeDispLeft,
    playerControlCurrentTimeDispRight,
    isMovieSearchResultModeDetail,
    isMovieSearchResultModeArea,
    setMoviePlayerControlDispVals,
  });
}
